import { useEffect, useMemo, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { findAll } from "./Aula/api-cronograma-aula";
import { timeFormat } from "../../util/DateUtil";
import { errorMessage } from "../../util/ConfirmAlertUtil";
import { splitUtil } from "../../util/StringUtil";
import { DIAS_SEMANA, mensagemDe } from "./cronogramaUtil";

// Linhas da grade em ordem de horário de início ("HH:mm" ordena como texto);
// a ordem da aula só desempata.
const porHorario = (a, b) =>
  timeFormat(a.horarioInicio).localeCompare(timeFormat(b.horarioInicio)) ||
  (a.ordemAula ?? 0) - (b.ordemAula ?? 0);

// Linha de detalhe padrão (visão da turma): professor e local.
const detalheProfessorLocal = (c) =>
  [c.tdp?.usuario ? splitUtil(c.tdp.usuario.nome, " ", 2) : "", c.local?.descricao ?? ""].filter(Boolean).join(" · ");

/**
 * Grade semanal: linhas por horário de aula e colunas por dia (segunda a sexta).
 * `detalhe(c)` define a segunda linha de cada célula (padrão: professor · local).
 */
export default function CronogramaTableV2({
  cronogramas,
  detalhe = detalheProfessorLocal,
  emptyText = "Nenhuma aula cadastrada neste período.",
}) {
  const [aulas, setAulas] = useState([]);

  useEffect(() => {
    findAll().then((data) => {
      if (Array.isArray(data)) setAulas([...data].sort(porHorario));
      else if (data?.error) errorMessage(mensagemDe(data));
    });
  }, []);

  // Indexa por aula e dia; antes as células eram colocadas em sequência e
  // desalinhavam quando faltava aula em algum dia.
  const porAulaEDia = useMemo(() => {
    const mapa = new Map();
    cronogramas.forEach((c) => {
      const chave = `${c.cronogramaAula?.codigo}-${c.diaSemana}`;
      mapa.set(chave, [...(mapa.get(chave) ?? []), c]);
    });
    return mapa;
  }, [cronogramas]);

  const aulasComCronograma = aulas.filter((aula) => cronogramas.some((c) => c.cronogramaAula?.codigo === aula.codigo));

  if (aulasComCronograma.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" textAlign="center" py={6}>
        {emptyText}
      </Typography>
    );
  }

  return (
    <TableContainer sx={{ border: 1, borderColor: "divider", borderRadius: 2 }}>
      <Table size="small" sx={{ minWidth: 720, tableLayout: "fixed" }} aria-label="Grade semanal do cronograma">
        <TableHead>
          <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover", textAlign: "center" } }}>
            <TableCell sx={{ width: 110 }}>Horário</TableCell>
            {DIAS_SEMANA.map((dia) => (
              <TableCell key={dia.value}>{dia.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {aulasComCronograma.map((aula) => (
            <TableRow key={aula.codigo}>
              <TableCell sx={{ textAlign: "center", bgcolor: "action.hover" }}>
                <Typography variant="caption" fontWeight={600} component="div">
                  {aula.ordemAula}ª aula
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {timeFormat(aula.horarioInicio)} - {timeFormat(aula.horarioFim)}
                </Typography>
              </TableCell>
              {DIAS_SEMANA.map((dia) => {
                const itens = porAulaEDia.get(`${aula.codigo}-${dia.value}`) ?? [];
                return (
                  <TableCell key={dia.value} sx={{ textAlign: "center", verticalAlign: "top" }}>
                    {itens.length === 0 ? (
                      <Typography variant="caption" color="text.disabled">
                        --
                      </Typography>
                    ) : (
                      itens.map((c) => (
                        <div key={c.codigo ?? `${c.tdp?.codigo}-${dia.value}`}>
                          <Typography variant="caption" fontWeight={600} component="div" noWrap title={c.tdp?.disciplina?.descricao}>
                            {c.tdp?.disciplina?.descricao ?? "--"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="div" noWrap>
                            {detalhe(c)}
                          </Typography>
                        </div>
                      ))
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
